import { Component, OnInit } from '@angular/core';
import * as XLSX from 'xlsx';
import {
  CryptoScreeningService,
  ScreeningFiltros,
  ScreeningRegistro,
} from '../../services/crypto-screening.service';

/**
 * Panel de operador: consultar el riesgo de una wallet y revisar el historial
 * de decisiones que tomó el gate.
 *
 * La consulta puntual SÍ le pega a Chainalysis y se paga, por eso va detrás de
 * un botón. El historial lee lo ya guardado y no consulta nada.
 */
@Component({
  selector: 'app-validar-wallet',
  templateUrl: './validar-wallet.component.html',
  styleUrls: ['./validar-wallet.component.sass'],
})
export class ValidarWalletComponent implements OnInit {
  // ── Consulta puntual ───────────────────────────────────────────────
  direccion = '';
  consultando = false;
  resultado: any = null;
  errorConsulta: string | null = null;

  // ── Historial ──────────────────────────────────────────────────────
  filtros: ScreeningFiltros = { page: 1, limit: 25 };
  filtroWallet = '';
  filtroUserId = '';
  filtroDecision = '';
  filtroContexto = '';

  registros: ScreeningRegistro[] = [];
  cargandoHistorial = false;
  errorHistorial: string | null = null;
  total = 0;
  page = 1;
  totalPages = 1;

  readonly decisiones = ['PERMITIDO', 'BLOQUEADO', 'REVISION', 'ERROR'];
  readonly contextos = ['REGISTRO', 'ENVIO'];

  // Exportacion
  exportando = false;
  errorExport: string | null = null;

  /** Tope duro de filas a exportar, para no colgar el navegador. */
  private readonly MAX_EXPORT = 5000;
  /** Maximo que acepta el backend por pagina. */
  private readonly PAGINA_EXPORT = 100;

  constructor(private screeningService: CryptoScreeningService) {}

  ngOnInit(): void {
    this.buscarHistorial();
  }

  // ── Consulta puntual ───────────────────────────────────────────────

  async consultar(): Promise<void> {
    const address = this.direccion.trim();
    if (!address || this.consultando) return;

    this.consultando = true;
    this.resultado = null;
    this.errorConsulta = null;

    try {
      this.resultado = await this.screeningService.consultarRiesgo(address);
    } catch (error: any) {
      this.errorConsulta =
        error?.error?.message ||
        error?.message ||
        'No se pudo consultar la dirección.';
    } finally {
      this.consultando = false;
    }
  }

  limpiarConsulta(): void {
    this.direccion = '';
    this.resultado = null;
    this.errorConsulta = null;
  }

  // ── Historial ──────────────────────────────────────────────────────

  /** Los filtros del formulario, tal como los espera el backend. */
  private filtrosActuales(page: number, limit: number): ScreeningFiltros {
    const filtros: ScreeningFiltros = {
      page,
      limit,
      wallet: this.filtroWallet.trim() || undefined,
      decision: this.filtroDecision || undefined,
      contexto: this.filtroContexto || undefined,
    };

    const userId = Number(this.filtroUserId);
    if (this.filtroUserId.trim() && Number.isFinite(userId)) {
      filtros.userId = userId;
    }
    return filtros;
  }

  async buscarHistorial(page: number = 1): Promise<void> {
    if (this.cargandoHistorial) return;

    this.cargandoHistorial = true;
    this.errorHistorial = null;

    const filtros = this.filtrosActuales(page, 25);

    try {
      const respuesta = await this.screeningService.historial(filtros);
      this.registros = respuesta.items;
      this.total = respuesta.total;
      this.page = respuesta.page;
      this.totalPages = respuesta.totalPages;
    } catch (error: any) {
      this.errorHistorial =
        error?.error?.message ||
        error?.message ||
        'No se pudo cargar el historial.';
      this.registros = [];
    } finally {
      this.cargandoHistorial = false;
    }
  }

  /**
   * Exporta TODO lo que matchea el filtro, no solo la pagina a la vista.
   *
   * Se pagina contra el backend porque el endpoint topea en 100 por pagina; se
   * corta en MAX_EXPORT para que un filtro vacio sobre una tabla grande no
   * cuelgue el navegador. No consulta a Chainalysis: sale todo de lo guardado.
   */
  async exportarExcel(): Promise<void> {
    if (this.exportando) return;

    this.exportando = true;
    this.errorExport = null;

    try {
      const filas: ScreeningRegistro[] = [];
      let pagina = 1;
      let totalPaginas = 1;

      do {
        const respuesta = await this.screeningService.historial(
          this.filtrosActuales(pagina, this.PAGINA_EXPORT),
        );
        filas.push(...respuesta.items);
        totalPaginas = respuesta.totalPages;
        pagina++;
      } while (pagina <= totalPaginas && filas.length < this.MAX_EXPORT);

      if (!filas.length) {
        this.errorExport = 'No hay nada para exportar con esos filtros.';
        return;
      }

      const recortado = filas.length >= this.MAX_EXPORT;

      // Encabezados en castellano: esto lo abre Compliance, no un dev.
      const datos = filas.slice(0, this.MAX_EXPORT).map((r) => ({
        Fecha: this.formatearFecha(r.createdAt),
        Usuario: r.userId,
        Wallet: r.walletAddress,
        Red: r.network,
        Contexto: r.contexto,
        Riesgo: r.risk || '',
        Decision: r.decision,
        Motivo: r.riskReason || '',
        Entidad: r.clusterName || '',
        'Categoria entidad': r.clusterCategory || '',
        Error: r.error || '',
        'Id operacion': r.referenciaId || '',
        'Desde cache': r.desdeCache ? 'SI' : 'NO',
        'Id screening': r.id,
      }));

      const hoja = XLSX.utils.json_to_sheet(datos);
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, 'Screenings');

      const sello = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      XLSX.writeFile(libro, `screening_wallets_${sello}.xlsx`);

      if (recortado) {
        this.errorExport = `Se exportaron las primeras ${this.MAX_EXPORT} filas. Afina los filtros para ver el resto.`;
      }
    } catch (error: any) {
      this.errorExport =
        error?.error?.message ||
        error?.message ||
        'No se pudo generar el archivo.';
    } finally {
      this.exportando = false;
    }
  }

  limpiarFiltros(): void {
    this.filtroWallet = '';
    this.filtroUserId = '';
    this.filtroDecision = '';
    this.filtroContexto = '';
    this.buscarHistorial(1);
  }

  // ── Presentación ───────────────────────────────────────────────────

  /** Colores por decisión: verde pasa, rojo frena, ámbar espera, gris no sabe. */
  colorDecision(decision: string): { fondo: string; texto: string } {
    switch (decision) {
      case 'PERMITIDO':
        return { fondo: '#f0fdf4', texto: '#166534' };
      case 'BLOQUEADO':
        return { fondo: '#fef2f2', texto: '#991b1b' };
      case 'REVISION':
        return { fondo: '#fffbeb', texto: '#92400e' };
      default:
        return { fondo: '#f3f4f6', texto: '#4b5563' };
    }
  }

  /** Misma escala que usa Chainalysis, de menor a mayor. */
  colorRiesgo(risk: string | null): { fondo: string; texto: string } {
    switch ((risk || '').toLowerCase()) {
      case 'low':
        return { fondo: '#f0fdf4', texto: '#166534' };
      case 'medium':
        return { fondo: '#fffbeb', texto: '#92400e' };
      case 'high':
        return { fondo: '#fff1eb', texto: '#9a3412' };
      case 'severe':
        return { fondo: '#fef2f2', texto: '#9f1239' };
      default:
        return { fondo: '#f3f4f6', texto: '#4b5563' };
    }
  }

  acortarWallet(wallet: string): string {
    if (!wallet || wallet.length <= 14) return wallet || '';
    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  }

  formatearFecha(iso: string): string {
    if (!iso) return '';
    const fecha = new Date(iso);
    if (isNaN(fecha.getTime())) return iso;
    return fecha.toLocaleString('es-CL', { timeZone: 'America/Santiago' });
  }

  /** Las exposiciones más grandes primero: es lo que explica el nivel. */
  get exposicionesOrdenadas(): Array<{ category: string; value: number }> {
    const exposures = this.resultado?.exposures;
    if (!Array.isArray(exposures)) return [];
    return [...exposures].sort((a, b) => (b?.value || 0) - (a?.value || 0));
  }

  get paginasVisibles(): number[] {
    const paginas: number[] = [];
    const desde = Math.max(1, this.page - 2);
    const hasta = Math.min(this.totalPages, desde + 4);
    for (let i = desde; i <= hasta; i++) paginas.push(i);
    return paginas;
  }
}
